import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CYRA_LOGO } from "@/lib/brand";
import api from "@/lib/api";
import {
  Zap, ShieldCheck, IdCard, Store, CalendarCog, GraduationCap, Wallet,
  BarChart3, ArrowRight, QrCode, MapPin, Star, CheckCircle2, Activity, Briefcase,
} from "lucide-react";

const MODULES = [
  { icon: IdCard, name: "Krida Pass", desc: "Verified sports workforce identity with QR, credentials, work history & ratings.", tone: "text-krida-crimson" },
  { icon: Briefcase, name: "Krida Work", desc: "Opportunity engine with explainable matching for every professional role.", tone: "text-krida-cobalt" },
  { icon: Store, name: "Krida Marketplace", desc: "Coach & official booking with Smart Assign and transparent scoring.", tone: "text-krida-emerald" },
  { icon: CalendarCog, name: "Krida Ops", desc: "Tournament workforce deployment, rostering, check-in & command centre.", tone: "text-krida-amber" },
  { icon: GraduationCap, name: "Krida Skills", desc: "Courses, certifications and configurable career pathways.", tone: "text-krida-crimson" },
  { icon: Wallet, name: "Krida Pay", desc: "Match fees, settlements, invoices and earnings — closing the employment loop.", tone: "text-krida-cobalt" },
  { icon: BarChart3, name: "Krida Intelligence", desc: "Workforce analytics, supply-demand & government dashboards.", tone: "text-krida-emerald" },
  { icon: ShieldCheck, name: "Krida Admin", desc: "Governance, verification, configuration & compliance.", tone: "text-krida-amber" },
];

const FLOW = ["Discover", "Verify", "Skill", "Deploy", "Manage", "Pay", "Evaluate", "Progress"];

export default function Landing() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-krida-bg text-slate-100">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={CYRA_LOGO} alt="CYRA" className="h-14 w-auto" />
            <div className="h-8 w-px bg-slate-300" />
            <div className="font-serif italic text-krida-crimson text-3xl leading-none">Krida</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-link" className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-krida-crimson transition-colors">Sign in</Link>
            <Link to="/register" data-testid="nav-register-link" className="px-4 py-2 text-sm font-semibold rounded-lg bg-krida-crimson hover:bg-krida-crimsonDark text-white transition-colors">Join Krida</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden krida-grid-bg">
        <div className="absolute inset-0 bg-gradient-to-b from-krida-bg/40 via-krida-bg/70 to-krida-bg" />
        <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <div className="overline text-krida-emerald mb-5 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" /> Powering the people who make sport happen
            </div>
            <h1 className="font-heading uppercase font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Behind Every Game<br /> Is A <span className="g-text">Workforce.</span>
            </h1>
            <p className="text-slate-400 text-lg mt-6 max-w-xl leading-relaxed">
              CYRA Krida is the operating infrastructure to identify, verify, skill, deploy, manage, pay and develop the professional workforce behind sport — coaches, referees, officials, physios, analysts and every operational role.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/register" data-testid="landing-hero-cta" className="group px-6 py-3.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold flex items-center gap-2 transition-all">
                Activate your Krida Pass <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" data-testid="landing-secondary-cta" className="px-6 py-3.5 rounded-xl border border-white/15 hover:bg-white/5 font-semibold transition-colors">
                Explore the OS
              </Link>
            </div>
            {stats && (
              <div className="grid grid-cols-4 gap-4 mt-12 max-w-lg">
                {[["Professionals", stats.professionals], ["Verified", stats.verified], ["Organisations", stats.organisations], ["Sports", stats.sports]].map(([l, v]) => (
                  <div key={l}>
                    <div className="font-mono text-2xl font-bold text-slate-50">{v}+</div>
                    <div className="overline text-[9px] text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Krida Pass preview */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="floaty relative w-full max-w-sm rounded-3xl p-6 bg-gradient-to-br from-krida-card to-krida-surface border border-krida-crimson/30 shadow-[0_0_60px_rgba(30,136,229,0.32)] overflow-hidden">
              <div className="scan-beam absolute left-0 h-0.5 w-full bg-gradient-to-r from-transparent via-krida-crimson to-transparent" />
              <div className="flex items-center justify-between mb-5">
                <div className="overline text-krida-crimson">Krida Pass</div>
                <ShieldCheck className="w-5 h-5 text-krida-emerald" />
              </div>
              <div className="flex items-center gap-4">
                <img src="https://i.pravatar.cc/300?img=12" alt="pro" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
                <div>
                  <div className="font-heading text-2xl font-bold">Meena</div>
                  <div className="text-slate-400 text-sm">Basketball Referee · Elite</div>
                  <div className="font-mono text-xs text-krida-emerald mt-1">KRD-100001</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-6 text-center">
                {[["Rating", "4.8", Star], ["Games", "161", Activity], ["Location", "Chennai", MapPin]].map(([l, v, Ic]) => (
                  <div key={l} className="rounded-lg bg-black/30 border border-white/5 py-2.5">
                    <Ic className="w-3.5 h-3.5 mx-auto text-slate-500 mb-1" />
                    <div className="font-mono text-sm font-bold">{v}</div>
                    <div className="overline text-[8px] text-slate-500">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center justify-between rounded-xl bg-black/40 border border-white/5 p-4">
                <div>
                  <div className="text-xs text-slate-400">Scan to verify</div>
                  <div className="flex items-center gap-1.5 mt-1 text-krida-emerald text-xs font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Krida Verified
                  </div>
                </div>
                <QrCode className="w-14 h-14 text-slate-200" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow ribbon */}
      <section className="border-y border-white/5 bg-krida-surface/40">
        <div className="max-w-7xl mx-auto px-5 py-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-heading uppercase tracking-wide">
          {FLOW.map((f, i) => (
            <React.Fragment key={f}>
              <span className="text-lg font-bold text-slate-300">{f}</span>
              {i < FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-krida-crimson" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <div className="text-center mb-14">
          <div className="overline text-krida-emerald mb-3">One connected operating system</div>
          <h2 className="font-heading uppercase font-extrabold text-4xl sm:text-5xl tracking-tight">Eight products. One workforce graph.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MODULES.map((m) => (
            <div key={m.name} className="krida-card p-6 transition-all duration-200 hover:-translate-y-1" data-testid={`module-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <m.icon className={`w-7 h-7 ${m.tone}`} />
              <h3 className="font-heading text-xl font-bold mt-4">{m.name}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-krida-crimson/20 bg-gradient-to-br from-krida-surface to-krida-card p-10 sm:p-16 text-center">
          <div className="absolute inset-0 krida-grid-bg opacity-40" />
          <div className="relative">
            <h2 className="font-heading uppercase font-extrabold text-4xl sm:text-5xl tracking-tight">Krida Powers It.</h2>
            <p className="text-slate-400 mt-4 max-w-xl mx-auto">Join the sports workforce operating system connecting professionals, organisations, opportunities, operations, training, payments and intelligence.</p>
            <Link to="/register" data-testid="landing-footer-cta" className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 rounded-xl bg-krida-crimson hover:bg-krida-crimsonDark font-semibold transition-colors">
              Get started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-slate-600 text-sm font-mono">
        <span className="text-slate-500">Human Capital. Reinvented.</span> — CYRA <span className="font-serif italic text-krida-crimson">Krida</span>, The Sports Workforce OS · Demo environment
      </footer>
    </div>
  );
}
